import type { Row } from "./types.js";

/**
 * 로어북/갤러리 영역(LorebooksView / GalleryView) UI 문자열.
 * 키 접두사: lore.* / gallery.*
 *
 * 용어 일관성: 로어북은 nav.lorebooks와 동일 용어(fr "Codex", es "Compendio(s)",
 * it "Codex", pt "Compêndio(s)", ru "Лорбук(и)", ar "كتب المعرفة" 등)를 유지한다.
 * en-GB는 의미 차이가 없으므로 생략(en-US 폴백).
 */
export const lorebookMessages = {
  // ── Gallery ───────────────────────────────────────────────────────────
  "gallery.count": {
    ko: "{count}개의 생성 이미지", "en-US": "{count} generated images",
    ja: "{count} 件の生成画像", zh: "{count} 张生成图像",
    fr: "{count} images générées", de: "{count} generierte Bilder",
    es: "{count} imágenes generadas", it: "{count} immagini generate",
    pt: "{count} imagens geradas", "pt-BR": "{count} imagens geradas",
    ru: "Сгенерировано изображений: {count}", ar: "{count} صورة مُولّدة",
    hi: "{count} जनित छवियाँ", id: "{count} gambar yang dihasilkan",
  },
  "gallery.promptPlaceholder": {
    ko: "이미지 프롬프트를 입력하세요 (기본 프로바이더 사용)",
    "en-US": "Enter an image prompt (uses the default provider)",
    ja: "画像プロンプトを入力してください（既定のプロバイダーを使用）",
    zh: "输入图像提示词（使用默认提供商）",
    fr: "Saisissez un prompt d'image (utilise le fournisseur par défaut)",
    de: "Bild-Prompt eingeben (verwendet den Standardanbieter)",
    es: "Escribe un prompt de imagen (usa el proveedor predeterminado)",
    it: "Inserisci un prompt per l'immagine (usa il provider predefinito)",
    pt: "Introduz um prompt de imagem (usa o fornecedor predefinido)",
    "pt-BR": "Digite um prompt de imagem (usa o provedor padrão)",
    ru: "Введите промпт для изображения (используется провайдер по умолчанию)",
    ar: "أدخل موجّه صورة (يستخدم المزوّد الافتراضي)",
    hi: "छवि प्रॉम्प्ट दर्ज करें (डिफ़ॉल्ट प्रोवाइडर का उपयोग)",
    id: "Masukkan prompt gambar (memakai penyedia bawaan)",
  },
  "gallery.generating": {
    ko: "생성 중...", "en-US": "Generating...", ja: "生成中...", zh: "生成中...",
    fr: "Génération...", de: "Wird generiert...", es: "Generando...", it: "Generazione...",
    pt: "A gerar...", "pt-BR": "Gerando...", ru: "Генерация...", ar: "جارٍ التوليد...",
    hi: "बनाया जा रहा है...", id: "Membuat...",
  },
  "gallery.generate": {
    ko: "생성", "en-US": "Generate", ja: "生成", zh: "生成", fr: "Générer",
    de: "Generieren", es: "Generar", it: "Genera", pt: "Gerar", "pt-BR": "Gerar",
    ru: "Создать", ar: "توليد", hi: "बनाएँ", id: "Buat",
  },
  "gallery.empty": {
    ko: "생성된 이미지가 모두 여기에 모여요. 위 입력창에서 프롬프트로 만들거나, NAI·ComfyUI·Gemini 등으로 만든 이미지를 채팅과 상관없이 한 번에 몰아볼 수 있어요.",
    "en-US": "Every image you generate gathers here. Create one with a prompt above, or browse images made with NAI, ComfyUI, Gemini and more — all in one place, regardless of which chat they came from.",
    ja: "生成した画像はすべてここに集まります。上の入力欄からプロンプトで作るほか、NAI・ComfyUI・Gemini などで作った画像もチャットを問わず一覧で見られます。",
    zh: "你生成的所有图像都会汇集到这里。可在上方输入框用提示词生成，也能把用 NAI、ComfyUI、Gemini 等制作的图像不分对话地一并查看。",
    fr: "Toutes vos images générées sont rassemblées ici. Créez-en une avec un prompt ci-dessus, ou parcourez celles faites avec NAI, ComfyUI, Gemini, etc., réunies au même endroit, quelle que soit la discussion d'origine.",
    de: "Alle generierten Bilder sammeln sich hier. Erstelle eines per Prompt oben oder durchstöbere Bilder aus NAI, ComfyUI, Gemini usw. – alle an einem Ort, unabhängig vom jeweiligen Chat.",
    es: "Todas tus imágenes generadas se reúnen aquí. Crea una con un prompt arriba o revisa las hechas con NAI, ComfyUI, Gemini y más, todas juntas sin importar de qué chat provienen.",
    it: "Tutte le immagini che generi si raccolgono qui. Creane una con un prompt sopra oppure sfoglia quelle realizzate con NAI, ComfyUI, Gemini e altri, tutte in un unico posto a prescindere dalla chat di origine.",
    pt: "Todas as imagens que geras reúnem-se aqui. Cria uma com um prompt acima ou consulta as feitas com NAI, ComfyUI, Gemini e outros, todas num só lugar, independentemente da conversa de origem.",
    "pt-BR": "Todas as imagens que você gera ficam reunidas aqui. Crie uma com um prompt acima ou veja as feitas com NAI, ComfyUI, Gemini e outros, todas em um só lugar, não importa de qual conversa vieram.",
    ru: "Все созданные изображения собираются здесь. Создайте новое по промпту выше или просматривайте картинки из NAI, ComfyUI, Gemini и других — в одном месте, независимо от чата.",
    ar: "تتجمّع هنا كل الصور التي تولّدها. أنشئ صورة عبر موجّه في الأعلى، أو تصفّح الصور المُنشأة بـ NAI وComfyUI وGemini وغيرها في مكان واحد بغضّ النظر عن المحادثة.",
    hi: "आपकी बनाई सभी छवियाँ यहीं इकट्ठा होती हैं। ऊपर प्रॉम्प्ट से बनाएँ, या NAI, ComfyUI, Gemini आदि से बनी छवियाँ किसी भी चैट से स्वतंत्र रूप से एक ही जगह देखें।",
    id: "Semua gambar yang kamu hasilkan terkumpul di sini. Buat lewat prompt di atas, atau telusuri gambar dari NAI, ComfyUI, Gemini, dan lainnya dalam satu tempat, terlepas dari obrolan asalnya.",
  },
  "gallery.imageAlt": {
    ko: "생성 이미지", "en-US": "Generated image", ja: "生成画像", zh: "生成图像",
    fr: "Image générée", de: "Generiertes Bild", es: "Imagen generada", it: "Immagine generata",
    pt: "Imagem gerada", "pt-BR": "Imagem gerada", ru: "Сгенерированное изображение",
    ar: "صورة مُولّدة", hi: "जनित छवि", id: "Gambar yang dihasilkan",
  },
  "gallery.close": {
    ko: "닫기", "en-US": "Close", ja: "閉じる", zh: "关闭", fr: "Fermer",
    de: "Schließen", es: "Cerrar", it: "Chiudi", pt: "Fechar", "pt-BR": "Fechar",
    ru: "Закрыть", ar: "إغلاق", hi: "बंद करें", id: "Tutup",
  },
  "gallery.paramPrompt": {
    ko: "프롬프트", "en-US": "Prompt", ja: "プロンプト", zh: "提示词", fr: "Prompt",
    de: "Prompt", es: "Prompt", it: "Prompt", pt: "Prompt", "pt-BR": "Prompt",
    ru: "Промпт", ar: "الموجّه", hi: "प्रॉम्प्ट", id: "Prompt",
  },
  "gallery.paramNegative": {
    ko: "네거티브", "en-US": "Negative", ja: "ネガティブ", zh: "负面提示", fr: "Négatif",
    de: "Negativ", es: "Negativo", it: "Negativo", pt: "Negativo", "pt-BR": "Negativo",
    ru: "Негатив", ar: "السلبي", hi: "नेगेटिव", id: "Negatif",
  },
  "gallery.paramProvider": {
    ko: "프로바이더", "en-US": "Provider", ja: "プロバイダー", zh: "提供商", fr: "Fournisseur",
    de: "Anbieter", es: "Proveedor", it: "Provider", pt: "Fornecedor", "pt-BR": "Provedor",
    ru: "Провайдер", ar: "المزوّد", hi: "प्रोवाइडर", id: "Penyedia",
  },
  "gallery.paramModel": {
    ko: "모델", "en-US": "Model", ja: "モデル", zh: "模型", fr: "Modèle",
    de: "Modell", es: "Modelo", it: "Modello", pt: "Modelo", "pt-BR": "Modelo",
    ru: "Модель", ar: "النموذج", hi: "मॉडल", id: "Model",
  },
  "gallery.paramSeed": {
    ko: "시드", "en-US": "Seed", ja: "シード", zh: "种子", fr: "Seed",
    de: "Seed", es: "Semilla", it: "Seed", pt: "Seed", "pt-BR": "Seed",
    ru: "Сид", ar: "البذرة", hi: "सीड", id: "Seed",
  },
  "gallery.paramSteps": {
    ko: "스텝", "en-US": "Steps", ja: "ステップ", zh: "步数", fr: "Étapes",
    de: "Schritte", es: "Pasos", it: "Passi", pt: "Passos", "pt-BR": "Passos",
    ru: "Шаги", ar: "الخطوات", hi: "स्टेप्स", id: "Langkah",
  },

  // ── Lorebooks ─────────────────────────────────────────────────────────
  "lore.posBeforeChar": {
    ko: "캐릭터 설명 앞", "en-US": "Before character description",
    ja: "キャラクター説明の前", zh: "角色描述之前",
    fr: "Avant la description du personnage", de: "Vor der Charakterbeschreibung",
    es: "Antes de la descripción del personaje", it: "Prima della descrizione del personaggio",
    pt: "Antes da descrição do personagem", "pt-BR": "Antes da descrição do personagem",
    ru: "Перед описанием персонажа", ar: "قبل وصف الشخصية",
    hi: "पात्र विवरण से पहले", id: "Sebelum deskripsi karakter",
  },
  "lore.posAfterChar": {
    ko: "캐릭터 설명 뒤", "en-US": "After character description",
    ja: "キャラクター説明の後", zh: "角色描述之后",
    fr: "Après la description du personnage", de: "Nach der Charakterbeschreibung",
    es: "Después de la descripción del personaje", it: "Dopo la descrizione del personaggio",
    pt: "Depois da descrição do personagem", "pt-BR": "Depois da descrição do personagem",
    ru: "После описания персонажа", ar: "بعد وصف الشخصية",
    hi: "पात्र विवरण के बाद", id: "Setelah deskripsi karakter",
  },
  "lore.posBeforeAn": {
    ko: "작가 노트 앞", "en-US": "Before author's note",
    ja: "作者ノートの前", zh: "作者注释之前",
    fr: "Avant la note d'auteur", de: "Vor der Autorennotiz",
    es: "Antes de la nota del autor", it: "Prima della nota dell'autore",
    pt: "Antes da nota do autor", "pt-BR": "Antes da nota do autor",
    ru: "Перед заметкой автора", ar: "قبل ملاحظة المؤلف",
    hi: "लेखक टिप्पणी से पहले", id: "Sebelum catatan penulis",
  },
  "lore.posAfterAn": {
    ko: "작가 노트 뒤", "en-US": "After author's note",
    ja: "作者ノートの後", zh: "作者注释之后",
    fr: "Après la note d'auteur", de: "Nach der Autorennotiz",
    es: "Después de la nota del autor", it: "Dopo la nota dell'autore",
    pt: "Depois da nota do autor", "pt-BR": "Depois da nota do autor",
    ru: "После заметки автора", ar: "بعد ملاحظة المؤلف",
    hi: "लेखक टिप्पणी के बाद", id: "Setelah catatan penulis",
  },
  "lore.posAtDepth": {
    ko: "특정 깊이", "en-US": "At specific depth",
    ja: "指定の深さ", zh: "指定深度",
    fr: "À une profondeur précise", de: "In bestimmter Tiefe",
    es: "A una profundidad específica", it: "A una profondità specifica",
    pt: "A uma profundidade específica", "pt-BR": "Em profundidade específica",
    ru: "На заданной глубине", ar: "عند عمق محدّد",
    hi: "विशिष्ट गहराई पर", id: "Pada kedalaman tertentu",
  },
  "lore.newBook": {
    ko: "새 로어북", "en-US": "New lorebook", ja: "新しいロアブック", zh: "新建设定集",
    fr: "Nouveau Codex", de: "Neues Lorebook", es: "Nuevo compendio", it: "Nuovo Codex",
    pt: "Novo compêndio", "pt-BR": "Novo compêndio", ru: "Новый лорбук",
    ar: "كتاب معرفة جديد", hi: "नया लोरबुक", id: "Lorebook baru",
  },
  "lore.deleteConfirm": {
    ko: "\"{name}\" 로어북을 삭제할까요?", "en-US": "Delete lorebook \"{name}\"?",
    ja: "ロアブック「{name}」を削除しますか？", zh: "删除设定集“{name}”？",
    fr: "Supprimer le Codex « {name} » ?", de: "Lorebook „{name}“ löschen?",
    es: "¿Eliminar el compendio «{name}»?", it: "Eliminare il Codex «{name}»?",
    pt: "Eliminar o compêndio \"{name}\"?", "pt-BR": "Excluir o compêndio \"{name}\"?",
    ru: "Удалить лорбук «{name}»?", ar: "حذف كتاب المعرفة \"{name}\"؟",
    hi: "लोरबुक \"{name}\" हटाएँ?", id: "Hapus lorebook \"{name}\"?",
  },
  "lore.imported": {
    ko: "{n}개 로어북 가져옴.", "en-US": "Imported {n} lorebook(s).",
    ja: "{n} 件のロアブックを取り込みました。", zh: "已导入 {n} 个设定集。",
    fr: "{n} Codex importé(s).", de: "{n} Lorebook(s) importiert.",
    es: "{n} compendio(s) importado(s).", it: "{n} Codex importato/i.",
    pt: "{n} compêndio(s) importado(s).", "pt-BR": "{n} compêndio(s) importado(s).",
    ru: "Импортировано лорбуков: {n}.", ar: "تم استيراد {n} من كتب المعرفة.",
    hi: "{n} लोरबुक आयात किए गए।", id: "{n} lorebook diimpor.",
  },
  "lore.count": {
    ko: "{count}개의 로어북", "en-US": "{count} lorebooks",
    ja: "{count} 件のロアブック", zh: "{count} 个设定集",
    fr: "{count} Codex", de: "{count} Lorebooks",
    es: "{count} compendios", it: "{count} Codex",
    pt: "{count} compêndios", "pt-BR": "{count} compêndios",
    ru: "Лорбуков: {count}", ar: "{count} من كتب المعرفة",
    hi: "{count} लोरबुक", id: "{count} lorebook",
  },
  "lore.importTitle": {
    ko: "world info JSON 가져오기", "en-US": "Import world info JSON",
    ja: "ワールドインフォ JSON を取り込む", zh: "导入世界信息 JSON",
    fr: "Importer un JSON d'infos du monde", de: "Welt-Info-JSON importieren",
    es: "Importar JSON de info del mundo", it: "Importa JSON di info del mondo",
    pt: "Importar JSON de info do mundo", "pt-BR": "Importar JSON de info do mundo",
    ru: "Импорт JSON world info", ar: "استيراد ملف JSON لمعلومات العالم",
    hi: "वर्ल्ड इन्फो JSON आयात करें", id: "Impor JSON info dunia",
  },
  "lore.empty": {
    ko: "로어북이 없어요. 세계관·설정을 키워드로 등록해 두면 대화 중 자동으로 프롬프트에 주입됩니다. 다른 앱의 world info JSON을 “가져오기”로 불러올 수도 있어요.",
    "en-US": "No lorebooks yet. Register your world and lore under keywords and they're injected into the prompt automatically during chats. You can also import world info JSON from other apps via “Import”.",
    ja: "ロアブックがありません。世界観・設定をキーワードで登録しておくと、会話中に自動でプロンプトへ注入されます。他アプリのワールドインフォ JSON を「インポート」で読み込むこともできます。",
    zh: "还没有设定集。把世界观与设定按关键词登记，对话时会自动注入提示词。也可用“导入”载入其他应用的世界信息 JSON。",
    fr: "Aucun Codex pour l'instant. Enregistrez votre univers et vos détails par mots-clés : ils seront injectés automatiquement dans le prompt pendant les discussions. Vous pouvez aussi importer un JSON d'infos du monde d'autres applis via « Importer ».",
    de: "Noch keine Lorebooks. Erfasse Welt und Hintergrund per Schlüsselwörtern – sie werden während Chats automatisch in den Prompt eingefügt. Du kannst auch Welt-Info-JSON aus anderen Apps über „Importieren“ laden.",
    es: "Aún no hay compendios. Registra tu mundo y tus detalles por palabras clave y se inyectarán automáticamente en el prompt durante los chats. También puedes importar JSON de info del mundo de otras apps con «Importar».",
    it: "Nessun Codex per ora. Registra il tuo mondo e le ambientazioni con parole chiave: verranno iniettati automaticamente nel prompt durante le chat. Puoi anche importare un JSON di info del mondo da altre app con «Importa».",
    pt: "Ainda não há compêndios. Regista o teu mundo e cenário por palavras-chave e serão injetados automaticamente no prompt durante as conversas. Também podes importar JSON de info do mundo de outras apps com «Importar».",
    "pt-BR": "Ainda não há compêndios. Cadastre seu mundo e cenário por palavras-chave e eles serão injetados automaticamente no prompt durante as conversas. Você também pode importar JSON de info do mundo de outros apps com “Importar”.",
    ru: "Пока нет лорбуков. Запишите мир и детали по ключевым словам — они автоматически добавятся в промпт во время чатов. Можно также импортировать JSON world info из других приложений через «Импорт».",
    ar: "لا توجد كتب معرفة بعد. سجّل عالمك وتفاصيله بكلمات مفتاحية لتُحقن تلقائيًا في الموجّه أثناء المحادثات. يمكنك أيضًا استيراد ملف JSON لمعلومات العالم من تطبيقات أخرى عبر «استيراد».",
    hi: "अभी कोई लोरबुक नहीं है। अपनी दुनिया और सेटिंग को कीवर्ड के रूप में दर्ज करें, ताकि बातचीत के दौरान वे स्वतः प्रॉम्प्ट में जुड़ जाएँ। आप अन्य ऐप्स की वर्ल्ड इन्फो JSON को “आयात” से भी ला सकते हैं।",
    id: "Belum ada lorebook. Daftarkan dunia dan latar lewat kata kunci agar otomatis disuntikkan ke prompt saat mengobrol. Kamu juga bisa mengimpor JSON info dunia dari aplikasi lain lewat “Impor”.",
  },
  "lore.entryCount": {
    ko: "{count}개 엔트리", "en-US": "{count} entries",
    ja: "{count} 件のエントリ", zh: "{count} 个条目",
    fr: "{count} entrées", de: "{count} Einträge",
    es: "{count} entradas", it: "{count} voci",
    pt: "{count} entradas", "pt-BR": "{count} entradas",
    ru: "Записей: {count}", ar: "{count} مدخلات",
    hi: "{count} प्रविष्टियाँ", id: "{count} entri",
  },
  "lore.backToList": {
    ko: "목록으로", "en-US": "Back to list", ja: "一覧へ戻る", zh: "返回列表",
    fr: "Retour à la liste", de: "Zurück zur Liste", es: "Volver a la lista", it: "Torna all'elenco",
    pt: "Voltar à lista", "pt-BR": "Voltar à lista", ru: "К списку",
    ar: "العودة إلى القائمة", hi: "सूची पर लौटें", id: "Kembali ke daftar",
  },
  "lore.addEntry": {
    ko: "엔트리 추가", "en-US": "Add entry", ja: "エントリを追加", zh: "添加条目",
    fr: "Ajouter une entrée", de: "Eintrag hinzufügen", es: "Añadir entrada", it: "Aggiungi voce",
    pt: "Adicionar entrada", "pt-BR": "Adicionar entrada", ru: "Добавить запись",
    ar: "إضافة مدخل", hi: "प्रविष्टि जोड़ें", id: "Tambah entri",
  },
  "lore.entriesEmpty": {
    ko: "엔트리가 없어요. “엔트리 추가”로 키워드와 내용을 등록하세요.",
    "en-US": "No entries yet. Use “Add entry” to register keywords and content.",
    ja: "エントリがありません。「エントリを追加」でキーワードと内容を登録してください。",
    zh: "还没有条目。用“添加条目”登记关键词和内容。",
    fr: "Aucune entrée. Utilisez « Ajouter une entrée » pour enregistrer des mots-clés et du contenu.",
    de: "Noch keine Einträge. Nutze „Eintrag hinzufügen“, um Schlüsselwörter und Inhalt zu erfassen.",
    es: "Aún no hay entradas. Usa «Añadir entrada» para registrar palabras clave y contenido.",
    it: "Nessuna voce. Usa «Aggiungi voce» per registrare parole chiave e contenuto.",
    pt: "Ainda não há entradas. Usa «Adicionar entrada» para registar palavras-chave e conteúdo.",
    "pt-BR": "Ainda não há entradas. Use “Adicionar entrada” para cadastrar palavras-chave e conteúdo.",
    ru: "Записей пока нет. Нажмите «Добавить запись», чтобы задать ключевые слова и содержимое.",
    ar: "لا توجد مدخلات بعد. استخدم «إضافة مدخل» لتسجيل الكلمات المفتاحية والمحتوى.",
    hi: "अभी कोई प्रविष्टि नहीं है। कीवर्ड और सामग्री दर्ज करने के लिए “प्रविष्टि जोड़ें” का उपयोग करें।",
    id: "Belum ada entri. Gunakan “Tambah entri” untuk mendaftarkan kata kunci dan konten.",
  },
  "lore.keysPlaceholder": {
    ko: "키워드 (쉼표로 구분)", "en-US": "Keywords (comma-separated)",
    ja: "キーワード（カンマ区切り）", zh: "关键词（用逗号分隔）",
    fr: "Mots-clés (séparés par des virgules)", de: "Schlüsselwörter (kommagetrennt)",
    es: "Palabras clave (separadas por comas)", it: "Parole chiave (separate da virgole)",
    pt: "Palavras-chave (separadas por vírgulas)", "pt-BR": "Palavras-chave (separadas por vírgulas)",
    ru: "Ключевые слова (через запятую)", ar: "الكلمات المفتاحية (مفصولة بفواصل)",
    hi: "कीवर्ड (अल्पविराम से अलग)", id: "Kata kunci (pisahkan dengan koma)",
  },
  "lore.contentPlaceholder": {
    ko: "이 키워드가 등장하면 주입할 내용 (RoQeet 인라인 값 사용 가능)",
    "en-US": "Content to inject when this keyword appears (RoQeet inline values supported)",
    ja: "このキーワードが現れたとき注入する内容（RoQeet インライン値対応）",
    zh: "当此关键词出现时注入的内容（支持 RoQeet 内联值）",
    fr: "Contenu à injecter quand ce mot-clé apparaît (valeurs RoQeet prises en charge)",
    de: "Inhalt, der bei Auftreten dieses Schlüsselworts eingefügt wird (RoQeet-Inlinewerte unterstützt)",
    es: "Contenido a inyectar cuando aparezca esta palabra clave (valores RoQeet compatibles)",
    it: "Contenuto da iniettare quando appare questa parola chiave (valori RoQeet supportati)",
    pt: "Conteúdo a injetar quando esta palavra-chave aparecer (valores RoQeet compatíveis)",
    "pt-BR": "Conteúdo a injetar quando esta palavra-chave aparecer (valores RoQeet compatíveis)",
    ru: "Содержимое для вставки при появлении ключевого слова (inline-значения RoQeet поддерживаются)",
    ar: "المحتوى الذي يُحقن عند ظهور هذه الكلمة المفتاحية (يدعم قيم RoQeet)",
    hi: "यह कीवर्ड आने पर inject content (RoQeet inline values समर्थित)",
    id: "Konten yang disuntikkan saat kata kunci ini muncul (mendukung nilai inline RoQeet)",
  },
  "lore.position": {
    ko: "위치", "en-US": "Position", ja: "位置", zh: "位置", fr: "Position",
    de: "Position", es: "Posición", it: "Posizione", pt: "Posição", "pt-BR": "Posição",
    ru: "Позиция", ar: "الموضع", hi: "स्थिति", id: "Posisi",
  },
  "lore.priority": {
    ko: "우선순위", "en-US": "Priority", ja: "優先度", zh: "优先级", fr: "Priorité",
    de: "Priorität", es: "Prioridad", it: "Priorità", pt: "Prioridade", "pt-BR": "Prioridade",
    ru: "Приоритет", ar: "الأولوية", hi: "प्राथमिकता", id: "Prioritas",
  },
  "lore.alwaysInject": {
    ko: "항상 주입", "en-US": "Always inject", ja: "常に注入", zh: "始终注入",
    fr: "Toujours injecter", de: "Immer einfügen", es: "Inyectar siempre", it: "Inietta sempre",
    pt: "Injetar sempre", "pt-BR": "Sempre injetar", ru: "Всегда вставлять",
    ar: "حقن دائمًا", hi: "हमेशा इंजेक्ट करें", id: "Selalu suntikkan",
  },
  "lore.entryDisabled": {
    ko: "비활성", "en-US": "Disabled", ja: "無効", zh: "停用", fr: "Désactivé",
    de: "Deaktiviert", es: "Desactivado", it: "Disattivato", pt: "Desativado", "pt-BR": "Desativado",
    ru: "Отключено", ar: "معطّل", hi: "अक्षम", id: "Nonaktif",
  },
} satisfies Record<string, Row>;
